import React, { useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { FileUploadV2 } from './FileUploadV2';
import {
  Container,
  Header,
  InstructionText,
  addBtnStyles,
  uploadBtnStyles,
} from './ProviderFIleUploadV2.styles';

import providerTemplate from '../../../assets/Files/ProviderUploadFileTemplate_v2.xlsx';
import { ButtonV2 } from '../../../components/common/v2/ButtonV2/ButtonV2';
import { TextFieldV2 } from '../../../components/common/v2/TextInputV2/TextInputV2';
import { isValidEmail } from '../../../utils/isEmailValid';

export const ProviderFileUploadV2 = ({ closeDrawer, switchToSingleLocation }) => {
  const [providerEmail, setProviderEmail] = useState();
  const [emailError, setEmailError] = useState('');

  const onEmailChange = useCallback((event) => {
    setEmailError();
    const email = event.target.value;

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      setProviderEmail('');
    }

    if (email) setProviderEmail(email);
  }, []);

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <ButtonV2 variant="contained" onClick={switchToSingleLocation} sx={addBtnStyles}>
          Add Location
        </ButtonV2>
        <IconButton onClick={closeDrawer}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Header>
        <Typography
          variant="h3"
          fontSize="40px"
          lineHeight="40px"
          fontWeight={600}
          fontFamily="Financier"
        >
          Add Multiple Location
        </Typography>
        <Tooltip
          title={
            <Typography color="white">
              Download the template file with the necessary data and upload it.
            </Typography>
          }
        >
          <InfoOutlinedIcon fontSize="large" color="primary" />
        </Tooltip>
      </Header>
      <ButtonV2
        href={providerTemplate}
        download="ProviderUploadFileTemplate_v2.xlsx"
        variant="contained"
        fullWidth
        sx={uploadBtnStyles}
      >
        Download The Provided Template
      </ButtonV2>
      <TextFieldV2
        label="Your Email Address"
        variant="outlined"
        fullWidth
        onBlur={onEmailChange}
        helperText={
          emailError || 'We will use this email only to communicate status of the file upload.'
        }
        error={!!emailError}
        sx={{ mb: 2 }}
      />
      <Typography
        variant="h3"
        fontSize="32px"
        lineHeight="32px"
        fontWeight={600}
        fontFamily="Financier"
      >
        Upload File
      </Typography>
      <FileUploadV2 providerEmail={providerEmail} />
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" fontSize="32px" fontWeight={600} fontFamily="Financier" gutterBottom>
        Instructions
      </Typography>
      <InstructionText>
        <p>
          <strong>Step 1:</strong> Download the provided &nbsp;
          <a href={providerTemplate} download="ProviderUploadFileTemplate_v2.xlsx">
            template
          </a>
          .
        </p>
        <p>
          <strong>Step 2</strong>: Complete the template with the necessary data and save it.{' '}
        </p>
        <ol type="1">
          <li>Ensure that the following mandatory fields are not left blank: </li>
          <ol type="1">
            <li>Provider Name</li>
            <li>Address</li>
            <li>City</li>
            <li>Region</li>
            <li>Country</li>
            <li>
              When entering country names, please use the following standardized formats. i.e.
            </li>
            <ul>
              <li>United States: Use "United States" instead of "USA"</li>
              <li>United Kingdom: Use "United Kingdom" instead of "UK"</li>
              <li>Canada: Use "Canada"</li>
              <li>Australia: Use "Australia"</li>
              <li>Germany: Use "Germany"</li>
            </ul>
            <li>For countries not listed here, use the full official country name.</li>
            <li>Provider</li>
            <li>Contact Email.</li>
          </ol>
          <li>Each amenity has a corresponding column; indicate availability by marking "Yes".</li>
          <li>Image Upload </li>
          <ul>
            <li>Single Location Upload</li>
            <ul>
              <li>
                Use the Single Location Upload if you are uploading images from a local drive.
              </li>
            </ul>
            <li>Multi Location Upload</li>
            <ul>
              <li>
                The Multi Location Upload functionality currently only supports images from Public
                URLs.
              </li>
            </ul>
          </ul>
          <li>Include the "Available date" in YYYY-MM-DD format.</li>
          <li>The file should contain no more than 1000 rows.</li>
        </ol>
        <p>
          <strong>Step 3</strong>: Enter your email address on the screen.
        </p>
        <p>
          <strong>Step 4</strong>: Upload the completed file. You'll receive an email notification
          once the process is finished.
        </p>
        <Typography variant="h4" fontSize="28px" fontWeight={600} fontFamily="Financier">
          Duplicate check (Override):
        </Typography>
        <ol>
          <li>Records are matched and deduplicated based on the following criteria:</li>
          <ul>
            <li>Provider Name</li>
            <li>Location Coordinates</li>
            <li>
              Ensure that both the provider’s name and location coordinates are accurate to
              facilitate proper duplicate checking.
            </li>
          </ul>
          <li>
            Mandatory attributes (address, city, country) will be replaced by values in the upload
            file during the override process.{' '}
          </li>
          <li>
            Optional attributes (zip, state, currency, price per month, availability date, region,
            email, amenities) will also be replaced.{' '}
          </li>
          <li>Records that have blank attribute values will not be updated.</li>
          <li>
            Additionally, optional attribute(images) will be appended during the override process.{' '}
          </li>
          <li>
            If the existing record contains values that the new record does not, those fields will
            remain unchanged. For instance, if the old record has 10 field values and the new one
            has 8 fields, the updated record will retain 10 fields.
          </li>
        </ol>
      </InstructionText>
    </Container>
  );
};
